import {
  Constant,
  ConstantKeyTranslation,
  ConstantLite,
  Constants,
  ConstantsLite,
  Croptype,
  HarvestGroupementType,
  MsSqlPaginatedData,
  UserDto,
} from '@agroone/entities'
import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { catchError, filter, forkJoin, map, Observable, of, tap } from 'rxjs'
import { API_URL } from '../models/environment.token'
import { requireDefined } from '../utils/require-defined.util'

/**
 * Shared agronomic constants service.
 *
 * Loads the constants/croptypes exposed by the common API (units, growth
 * stages, …) consumed by every front app. It only depends on the {@link API_URL}
 * token, so any app can inject it directly. Call {@link load} once at startup
 * with the current user to hydrate the store.
 */
@Injectable({
  providedIn: 'root',
})
export class SharedConstantService {
  protected http = inject(HttpClient)
  protected apiUrl? = inject(API_URL, { optional: true })

  private static readonly CONSTANTS_PATH: string = '/constants'
  private static readonly CROPTYPES_PATH: string = '/croptypes'

  /** Set from {@link API_URL} when it is provided; subclasses may define it themselves. */
  protected baseUrl?: string

  public croptypes: ConstantLite[] = []

  /** Loaded by {@link load}. */
  protected constants?: ConstantsLite

  protected currentUserLanguage?: string

  private readonly ALL: string = 'ALL'

  public harvestGroupementType: HarvestGroupementType[] = []

  constructor() {
    if (this.apiUrl) {
      this.baseUrl = `${this.apiUrl}${SharedConstantService.CONSTANTS_PATH}`
    }
  }

  /** Loads constants + croptypes for the given user's language into the store. */
  public load(user: UserDto | null): Observable<[ConstantsLite | undefined, ConstantLite[]]> {
    this.currentUserLanguage = user?.language
    const source$: Observable<[ConstantsLite, Croptype[]]> = user
      ? this._getAll(user.language)
      : of([new ConstantsLite(), []] as [ConstantsLite, Croptype[]])

    return source$.pipe(
      filter(Boolean),
      map(([constants, croptypes]: [ConstantsLite, Croptype[]]) => {
        for (const constantName of Object.keys(constants)) {
          const items = constants[constantName]
          if (Array.isArray(items) && items.length > 0 && typeof items[0]?.priority === 'number') {
            constants[constantName] = items.sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
          }
        }
        const mappedCroptypes: ConstantLite[] = []
        for (const croptype of croptypes) {
          mappedCroptypes.push({
            croptypeId: croptype.id,
            code: croptype.key,
            regions: croptype.regions,
            translation: croptype.translations?.[0]?.value ?? croptype.key,
          })
        }
        return [constants, mappedCroptypes] as [ConstantsLite, ConstantLite[]]
      }),
      tap(([constants]: [ConstantsLite, ConstantLite[]]) => {
        this.constants = constants
        this._setHarvestGroupement(constants)
      }),
      tap(([, croptypes]: [ConstantsLite, ConstantLite[]]) => {
        this.croptypes = croptypes
      }),
      catchError(() => of<[ConstantsLite | undefined, ConstantLite[]]>([this.constants, []])),
    )
  }

  protected _setHarvestGroupement(constant: ConstantsLite): void {
    if (constant?.harvestGroupementType?.length > 0) {
      this.harvestGroupementType = constant.harvestGroupementType.map(
        (harvestGroupementType) => new HarvestGroupementType(harvestGroupementType),
      )
    }
  }

  protected _getAll(language: string): Observable<[ConstantsLite, Croptype[]]> {
    const constantsOptions: { params: HttpParams } = {
      params: new HttpParams().set('languageName', language),
    }
    const croptypesOptions: { params: HttpParams } = {
      params: new HttpParams().set('filter', `languageName="${language}"`),
    }

    const constants$: Observable<ConstantsLite> = this.http
      .get<Constants>(requireDefined(this.baseUrl, 'the constants API URL'), constantsOptions)
      .pipe(map((constants: Constants) => this.constantsToConstantsLite(constants)))
    const croptypes$: Observable<Croptype[]> = this.http
      .get<MsSqlPaginatedData<Croptype>>(`${this.apiUrl}${SharedConstantService.CROPTYPES_PATH}`, croptypesOptions)
      .pipe(map((res: MsSqlPaginatedData<Croptype>) => res.data))

    return forkJoin([constants$, croptypes$])
  }

  protected _constantToConstantLite(constant: Constant): ConstantLite[] {
    const constantLites: ConstantLite[] = []
    for (const [key, value] of Object.entries(requireDefined(constant.values, 'the constant values'))) {
      const cropTypes = value?.cropTypes?.map((r) => r.name)
      // fall back to the code when no translation exists, matching the translate() method's behavior
      const translation: string =
        value?.translations?.find((t: ConstantKeyTranslation) => t.language === this.currentUserLanguage)?.value ?? key
      const constantLite: ConstantLite = {
        code: key,
        cropTypes,
        translation,
        ...value,
      } as unknown as ConstantLite
      constantLites.push(constantLite)
    }
    return constantLites
  }

  public get harvestGroupementKey(): ConstantLite[] {
    return this.constants?.harvestGroupementKey ?? []
  }

  public get sectors(): ConstantLite[] {
    return this.constants?.harvestGroupementKey?.filter((c) => c.type === 'sector') ?? []
  }

  public get cropType(): ConstantLite[] {
    if (this.constants) {
      return this.constants.cropType
    }
    return []
  }
  public get harvestQuality(): ConstantLite[] {
    if (this.constants) {
      return this.constants.harvestQuality
    }
    return []
  }
  public get areaUnit(): ConstantLite[] {
    if (this.constants) {
      return this.constants.areaUnit
    }
    return []
  }
  public get yieldUnit(): ConstantLite[] {
    if (this.constants) {
      return this.constants.yieldUnit
    }
    return []
  }

  public get preharvestControl(): ConstantLite[] {
    if (this.constants) {
      return this.constants.preharvestControl
    }
    return []
  }

  public get scoutingSubtype(): ConstantLite[] {
    if (this.constants) {
      return this.constants.scoutingSubtype
    }
    return []
  }

  public get foodSafety(): ConstantLite[] {
    if (this.constants) {
      return this.constants.foodSafety
    }
    return []
  }

  public get scoutingSubitem(): ConstantLite[] {
    if (this.constants) {
      return this.constants.scoutingSubitem
    }
    return []
  }

  public get area(): ConstantLite[] {
    if (this.constants) {
      return this.constants.area
    }
    return []
  }

  public get weatherDamage(): ConstantLite[] {
    if (this.constants) {
      return this.constants.weatherDamage
    }
    return []
  }
  public get pressure(): ConstantLite[] {
    if (this.constants) {
      return this.constants.pressure
    }
    return []
  }
  public get environment(): ConstantLite[] {
    if (this.constants) {
      return this.constants.environment
    }
    return []
  }
  public get growthStage(): ConstantLite[] {
    if (this.constants) {
      return this.constants.growthStage
    }
    return []
  }
  public get weightUnit(): ConstantLite[] {
    if (this.constants) {
      return this.constants.weightUnit
    }
    return []
  }
  public get qualityDefect(): ConstantLite[] {
    if (this.constants) {
      return this.constants.qualityDefect
    }
    return []
  }
  public get scoutingItem(): ConstantLite[] {
    if (this.constants) {
      return this.constants.scoutingItem
    }
    return []
  }
  public get stage(): ConstantLite[] {
    if (this.constants) {
      return this.constants.stage
    }
    return []
  }
  public get recommendationSubtype(): ConstantLite[] {
    if (this.constants) {
      return this.constants.recommendationSubtype
    }
    return []
  }
  public get commentItem(): ConstantLite[] {
    if (this.constants) {
      return this.constants.commentItem
    }
    return []
  }
  public get commentSubItem(): ConstantLite[] {
    if (this.constants) {
      return this.constants.commentSubItem
    }
    return []
  }

  public get previousCropType(): ConstantLite[] {
    return this.constants?.previousCropType ?? []
  }

  public get cropVigor(): ConstantLite[] {
    return this.constants?.cropVigor ?? []
  }

  public get scoutingType(): ConstantLite[] {
    return this.constants?.scoutingType ?? []
  }

  public get pesticideType(): ConstantLite[] {
    return this.constants?.pesticideType ?? []
  }

  public get soilConditions(): ConstantLite[] {
    return this.constants?.soilConditions ?? []
  }

  public get weatherConditions(): ConstantLite[] {
    return this.constants?.weatherConditions ?? []
  }

  public get interventionType(): ConstantLite[] {
    return this.constants?.interventionType ?? []
  }

  public get interventionSubtype(): ConstantLite[] {
    return this.constants?.interventionSubtype ?? []
  }

  public get soilTexture(): ConstantLite[] {
    return this.constants?.soilTexture ?? []
  }

  public get drainage(): ConstantLite[] {
    return this.constants?.drainage ?? []
  }

  public get drainageType(): ConstantLite[] {
    return this.constants?.drainageType ?? []
  }

  public get drainageWidth(): ConstantLite[] {
    return this.constants?.drainageWidth ?? []
  }

  public get drainageClassification(): ConstantLite[] {
    return this.constants?.drainageClassification ?? []
  }

  public get stonePressure(): ConstantLite[] {
    return this.constants?.stonePressure ?? []
  }

  public get irrigationEquipement(): ConstantLite[] {
    return this.constants?.irrigationEquipement ?? []
  }

  public get obstruction(): ConstantLite[] {
    return this.constants?.obstruction ?? []
  }

  public get topography(): ConstantLite[] {
    return this.constants?.topography ?? []
  }

  public get worklistTaskList(): ConstantLite[] {
    return this.constants?.worklistTaskList ?? []
  }

  public get sampleTest(): ConstantLite[] {
    return this.constants?.sampleTest ?? []
  }

  public get seedingRateUnit(): ConstantLite[] {
    return this.constants?.seedingRateUnit ?? []
  }

  public get legalStructures(): ConstantLite[] {
    return this.constants?.legalStructure ?? []
  }

  public get temperatureUnit(): ConstantLite[] {
    return this.constants?.temperatureUnit ?? []
  }

  public get preharvestDefect(): ConstantLite[] {
    return this.constants?.preharvestDefect ?? []
  }

  public get irrigationUnit(): ConstantLite[] {
    return this.constants?.irrigationUnit ?? []
  }

  public get lengthUnit(): ConstantLite[] {
    return this.constants?.lengthUnit ?? []
  }

  public get contactType(): ConstantLite[] {
    return this.constants?.contactType ?? []
  }

  public get growingSystem(): ConstantLite[] {
    return this.constants?.growingSystem ?? []
  }

  public get associatedCrop(): ConstantLite[] {
    return this.constants?.associatedCrop ?? []
  }

  public get alternativeSolution(): ConstantLite[] {
    return this.constants?.alternativeSolution ?? []
  }

  public get csrRegenAgriAnswer(): ConstantLite[] {
    return this.constants?.csrRegenAgriAnswer ?? []
  }

  public get rawMaterialProcessed(): ConstantLite[] {
    return this.constants?.rawMaterialProcessed ?? []
  }

  public get hypothesisType(): ConstantLite[] {
    return this.constants?.hypothesisType ?? []
  }

  public get productionArea(): ConstantLite[] {
    return this.constants?.productionArea ?? []
  }

  public get fieldType(): ConstantLite[] {
    return this.constants?.fieldType ?? []
  }

  public get windSpeedUnit(): ConstantLite[] {
    return this.constants?.windSpeedUnit ?? []
  }

  public get applicationEquipment(): ConstantLite[] {
    return this.constants?.applicationEquipment ?? []
  }

  public get applicator(): ConstantLite[] {
    return this.constants?.applicator ?? []
  }

  public get operationType(): ConstantLite[] {
    return this.constants?.operationType ?? []
  }

  public get invoiceInformation(): ConstantLite[] {
    return this.constants?.invoiceInformation ?? []
  }

  public get analysisType(): ConstantLite[] {
    return this.constants?.analysisType ?? []
  }

  public get fieldBuffer(): ConstantLite[] {
    return this.constants?.fieldBuffer ?? []
  }

  public get plantingContractor(): ConstantLite[] {
    return this.constants?.plantingContractor ?? []
  }

  /** Semi-contractors scoped by region and crop type (same rules as {@link getFilteredByRegionAndCroptype}). */
  public getPlantingContractors(regionName: string, cropType?: string): ConstantLite[] {
    return this.getFilteredByRegionAndCroptype(this.plantingContractor, regionName, cropType)
  }

  public get windDirection(): ConstantLite[] {
    return this.constants?.windDirection ?? []
  }

  public get targetPest(): ConstantLite[] {
    return this.constants?.targetPest ?? []
  }

  public get nutrientsQuantityUnit(): ConstantLite[] {
    return this.constants?.nutrientsQuantityUnit ?? []
  }

  public get waterSource(): ConstantLite[] {
    return this.constants?.waterSource ?? []
  }

  public get wildlifeEvidence(): ConstantLite[] {
    return this.constants?.wildlifeEvidence ?? []
  }

  public get obstructionSeverity(): ConstantLite[] {
    return this.constants?.obstructionSeverity ?? []
  }

  public get tillageCondition(): ConstantLite[] {
    return this.constants?.tillageCondition ?? []
  }

  public get varietalTypes(): ConstantLite[] {
    return this.constants?.varietalTypes ?? []
  }

  public get cooperatives(): ConstantLite[] {
    return this.constants?.cooperative ?? []
  }

  public get trucksType(): ConstantLite[] {
    return this.constants?.trucksType ?? []
  }

  public get farmType(): ConstantLite[] {
    return this.constants?.farmType ?? []
  }

  public get breakType(): ConstantLite[] {
    return this.constants?.breakType ?? []
  }

  public get cancelCause(): ConstantLite[] {
    return this.constants?.cancelCause ?? []
  }

  public get trialCause(): ConstantLite[] {
    return this.constants?.trialCause ?? []
  }

  public getContactFunctions(region: string): ConstantLite[] {
    const contactFunctions: ConstantLite[] = this.constants?.contactFunction ?? []
    const allowedFunctions: ConstantLite[] = contactFunctions.filter((f) => {
      const matched: boolean =
        requireDefined(f.regions, 'the contact function regions').findIndex(
          (r) => requireDefined(r.name, 'the region name').toLocaleLowerCase() === region.toLocaleLowerCase(),
        ) > -1
      return matched
    })
    return allowedFunctions
  }

  public translate(code: string | null | undefined, constants: ConstantLite[]): string {
    if (!Array.isArray(constants) || !code) {
      return code ?? ''
    }
    const c = constants.find(
      (constant: ConstantLite) =>
        requireDefined(constant.code, 'the constant code').toLowerCase() === code.toLowerCase(),
    )
    return c?.translation ?? code
  }

  protected constantsToConstantsLite(constants: Constants): ConstantsLite {
    const constantsLite: ConstantsLite = {}
    if (!constants || typeof constants !== 'object') {
      return constantsLite
    }
    for (const key of Object.keys(constants)) {
      const constant = constants[key]
      // A constant without values is left out: readers treat a missing constant as empty
      if (constant?.values) {
        constantsLite[key] = this._constantToConstantLite(constant)
      }
    }
    return constantsLite
  }

  public getAllGrowthStage(cropType?: string | null, regionId?: number | null): ConstantLite[] {
    return this.growthStage.filter((growthStage) => {
      let res: boolean = true

      if (cropType && growthStage.cropTypes) {
        res = !!growthStage.cropTypes?.find((tCropType) => tCropType.name?.toLowerCase() === cropType?.toLowerCase())
      }

      if (res && regionId && growthStage.regions) {
        res = !!growthStage.regions?.find((region) => region.id === regionId)
      }

      return res
    })
  }

  public getAllQualityDefect(cropType?: string | null, regionId?: number | null): ConstantLite[] {
    return this.qualityDefect.filter((qualityDefect) => {
      let res: boolean = true

      if (cropType && qualityDefect.cropTypes) {
        res = !!qualityDefect.cropTypes?.find(
          (tCropType) => requireDefined(tCropType.name, 'the crop type name').toLowerCase() === cropType?.toLowerCase(),
        )
      }

      if (res && regionId && qualityDefect.regions) {
        res = !!qualityDefect.regions?.find((region) => region.id === regionId)
      }

      return res
    })
  }

  public getAllScoutingItem(filters: { subType?: string; cropType?: string; regionName?: string }): ConstantLite[] {
    return this.scoutingItem.filter((scoutingItem) => {
      let res: boolean = true

      if (filters?.cropType && scoutingItem.cropTypes) {
        res = !!scoutingItem.cropTypes?.find(
          (tCropType) =>
            requireDefined(tCropType.name, 'the crop type name').toLowerCase() === filters.cropType?.toLowerCase(),
        )
      }

      if (res && filters?.subType && scoutingItem.subType) {
        res = scoutingItem.subType.toLowerCase() === filters.subType.toLowerCase() || scoutingItem.subType === this.ALL
      }

      if (res && filters?.regionName && scoutingItem.regions) {
        res = !!scoutingItem.regions?.find(
          (region) =>
            requireDefined(region.name, 'the region name').toLowerCase() === filters.regionName?.toLowerCase(),
        )
      }

      return res
    })
  }

  public getAllScoutingSubItem(filters: { item?: string }): ConstantLite[] {
    return this.scoutingSubitem.filter((scoutingItem) =>
      filters?.item && scoutingItem.item
        ? scoutingItem.item.toLowerCase() === filters.item.toLowerCase() || scoutingItem.item === this.ALL
        : true,
    )
  }

  public getFiltered(constants: ConstantLite[], type?: string | null, regionName?: string | null): ConstantLite[] {
    let filteredConstants: ConstantLite[] = constants
    if (type) {
      filteredConstants = constants.filter((c) => c.type === type)
    }
    if (regionName) {
      const normalizedRegion: string = regionName?.toLowerCase()
      filteredConstants = filteredConstants.filter((s) => {
        let res: boolean = true
        if (s.regions?.length) {
          res = s.regions?.some((r) => r.name?.toLowerCase() === normalizedRegion)
        }
        return res
      })
    }
    return filteredConstants.sort((x, y) => Number(x.priority) - Number(y.priority))
  }

  public getAllSeedingRateUnits(regionName: string | null | undefined): ConstantLite[] {
    return this.seedingRateUnit.filter((sru) => {
      let res: boolean = true
      if (res && regionName && sru.regions) {
        res = !!sru.regions?.find(
          (region) => requireDefined(region.name, 'the region name').toLowerCase() === regionName?.toLowerCase(),
        )
      }

      return res
    })
  }

  public getStages(subtype: string | null | undefined): ConstantLite[] {
    if (subtype) {
      return this.stage.filter((stage) => stage?.subType?.toLowerCase() === subtype.toLowerCase())
    }
    return []
  }

  public getConstantByName(constantName: string, language: string): Observable<ConstantLite[]> {
    let params: HttpParams = new HttpParams()
    if (language) {
      params = params.append('languageName', language)
    }
    const options: {
      params: HttpParams
    } = {
      params,
    }

    return this.http
      .get<Constant>(`${this.baseUrl}/${constantName}`, options)
      .pipe(map((res: Constant) => this._constantToConstantLite(res)))
  }

  public getFarmLegalStructures(regionName: string | null | undefined): ConstantLite[] {
    return this.legalStructures.filter((ls) => {
      const matched: boolean =
        requireDefined(ls.regions, 'the legal structure regions').findIndex((r) => r.name === regionName) > -1
      return matched
    })
  }

  public getFilteredByCroptype(constants: ConstantLite[], cropType: string | null | undefined): ConstantLite[] {
    return constants.filter((c) => {
      if (!c.cropTypes?.length) {
        return true
      }
      return c.cropTypes.some((r) => r.name === cropType)
    })
  }

  public getFilteredByRegionAndCroptype(
    constants: ConstantLite[],
    region: string | null | undefined,
    cropType?: string | null,
  ): ConstantLite[] {
    const filteredByRegion: ConstantLite[] = this.getFiltered(constants, undefined, region)

    return cropType ? this.getFilteredByCroptype(filteredByRegion, cropType) : filteredByRegion
  }

  public getCooperativeConstantKey(id: number | null | undefined): ConstantLite | undefined {
    return this.cooperatives.find((c) => c.id === id)
  }
}
